import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';

import { unhandledRequest } from 'api/request';
import { useStore } from 'models/Provider';

export type TaktikalToken = {
  flowKey: string;
  apiKey: string;
  companyKey: string;
};

export type TaktikalTokenError = {
  response: {
    data: {
      ok: boolean;
      message: string;
    };
  };
};

export type Cosigner = {
  id: number;
  name: string;
  email: string;
  ssn: string;
  phone: string;
};

export type EditCosigner = Omit<Cosigner, 'id'>;

export type CosignerError = {
  response: {
    data: {
      nonFieldErrors: Array<String>;
    };
  };
};

export type SignatureLocationOptions = 'TopFirstPage' | 'BottomLastPage';

export type SignatureLocation = {
  signatureLocation: SignatureLocationOptions;
};

export async function taktikalGetToken() {
  const response = await unhandledRequest.get<{ ok: boolean }>({
    url: `/integrations/taktikal/settings/token/`,
  });
  return response.data;
}

export async function taktikalSetToken(keys: TaktikalToken) {
  const response = await unhandledRequest.post<{ ok: boolean }>({
    url: `/integrations/taktikal/settings/token/`,
    data: keys,
  });
  return response.data;
}

export async function taktikalEditToken(keys: TaktikalToken) {
  const response = await unhandledRequest.patch<{ ok: boolean }>({
    url: `/integrations/taktikal/settings/token/`,
    data: keys,
  });
  return response.data;
}

export async function taktikalGetCosigners() {
  const response = await unhandledRequest.get<Cosigner[]>({
    url: `/integrations/taktikal/settings/cosigners/`,
  });
  return response.data;
}

export async function taktikalAddCosigner(data: EditCosigner) {
  const response = await unhandledRequest.post({
    url: `/integrations/taktikal/settings/cosigners/`,
    data,
  });
  return response.data;
}

export async function taktikalEditCosigner(cosignerId: string, data: EditCosigner) {
  const response = await unhandledRequest.patch({
    url: `/integrations/taktikal/settings/cosigners/${cosignerId}/`,
    data,
  });
  return response.data;
}

export async function taktikalRemoveCosigner(cosignerId: string) {
  const response = await unhandledRequest.delete({
    url: `/integrations/taktikal/settings/cosigners/${cosignerId}/`,
  });
  return response.data;
}

export const useTaktikalSignatureLocation = () => {
  return useQuery({
    queryKey: ['taktikal-signature-location'],
    queryFn: taktikalGetSignatureLocation,
  });
};

export async function taktikalGetSignatureLocation() {
  const response = await unhandledRequest.get<SignatureLocation>({
    url: `/integrations/taktikal/settings/signaturelocation/`,
  });
  return response.data;
}

export function updateTaktikalSignatureLocation(data: SignatureLocation) {
  return unhandledRequest.patch({
    url: '/integrations/taktikal/settings/signaturelocation/',
    data,
  });
}

export const useUpdateTaktikalSignatureLocation = () => {
  const { t } = useStore();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  return useMutation(updateTaktikalSignatureLocation, {
    onSuccess: () => {
      showSuccess(t('integrations.taktikal.signaturelocation.success'));
      return queryClient.invalidateQueries(['taktikal-signature-location']);
    },
    onError: () => showError(t('integrations.taktikal.signaturelocation.error')),
  });
};
